<template>
  <validation-observer
    v-if="isRenderAfterFullfilled"
    v-slot="observer"
    slim
  >
    <form-section
      id="vehicle-purchase"
      v-bind="observer"
      :validated="formObserver.validated && observer.failed"
    >
      <!-- VEHICLE BOUGHT -->
      <form-row
        id="vehicle-bought"
        :label="`Have you bought the ${['car', 'bike', 'motorhome', 'caravan'].includes(productName) ? productName : 'vehicle'} yet?`"
        :error="formObserver.validated && observer.failed"
      >
        <validation-provider
          :vid="`${stepId}_vehicle-bought`"
          name="Vehicle Bought"
          rules="required"
        >
          <radio
            v-model="questionData.vehiclePurchased"
            :data-test="fieldTrackerId.vehiclePurchased"
            name="vehicle_vehicle-bought"
            :items="yesNo.map(option => ({
              ...option,
              disabled: false
            }))"
            :error="formObserver.validated && observer.failed"
          />
        </validation-provider>
      </form-row>
      <!-- VEHICLE PURCHASE DATE -->
      <form-row
        v-if="questionData.vehiclePurchased"
        :question="hasLastSaleDate && productName !== 'caravan' ? 'Based on your vehicle registration details we may have completed this for you. Please check this date is correct.' : undefined"
        :help-text="productName !== 'caravan' ? 'You can find this date in your V5C registration document, also known as the \'log book\'.' : undefined"
        :error="formObserver.validated && observer.failed"
      >
        <form-section-question>Please enter the month and year of purchase:</form-section-question>
        <time-zone-warning
          v-if="isWarningTextShown"
          :question-date="questionData.vehicleBought"
        />
        <div class="dates">
          <div
            id="vehicle-bought_month"
            class="dates-col"
          >
            <validation-provider
              v-slot="{ failed }"
              :vid="`${stepId}_vehicle-bought_month`"
              name="Month of Purchase"
              rules="listOptionRequired"
            >
              <qz-select
                v-model="vehicleBought.month"
                :data-test="fieldTrackerId.month"
                :items="monthsOptions"
                placeholder="Select month"
                :error="failed || isVehiclePurchasedFailed"
              />
            </validation-provider>
          </div>
          <div
            id="vehicle-bought_year"
            class="dates-col"
          >
            <validation-provider
              v-slot="{ failed }"
              :vid="`${stepId}_vehicle-bought_year`"
              name="Year of Purchase"
              rules="listOptionRequired"
            >
              <qz-select
                v-model="vehicleBought.year"
                :data-test="fieldTrackerId.year"
                :items="vehicleBoughtYearOptions"
                placeholder="Select year"
                :error="failed || isVehiclePurchasedFailed"
              />
            </validation-provider>
          </div>
        </div>
        <validation-provider
          v-slot="{ errors, failed, failedRules }"
          :vid="`${stepId}_vehicle-bought`"
          name="Vehicle Bought"
          :rules="dateOfBoughtValidationRules"
          immediate
        >
          <input
            type="hidden"
            :value="questionData.vehicleBought"
          >
          <div
            v-if="failed && isVehiclePurchasedErrorShown"
            class="error--text mt-4 fz12"
          >
            {{ getFieldErrors('vehicleBought', failedRules, errors)[0] }}
          </div>
        </validation-provider>
      </form-row>
    </form-section>
  </validation-observer>
</template>

<script>
import QuestionBase from './Base';
import TimeZoneWarning from '@/components/TimeZoneWarning/TimeZoneWarning.vue';

import {
  monthsOptions,
} from '@/helpers/common';

import {
  ExtDate,
  UtcDate,
  checkDateObjFilled,
  getDayLessDateTemplate,
} from '@/helpers/date';

import { computed } from '@vue/composition-api';
import useTimeZoneWarning from '@/composables/useTimeZoneWarning.js';
import { snakeCase } from 'lodash';

export default {
  name: 'question-vehicle-purchase',
  extends: QuestionBase,
  components: {
    TimeZoneWarning,
  },
  props: {
    lastSaleDate: {
      type: Date,
      default: null,
    },
  },
  data () {
    return {
      fields: ['vehiclePurchased', 'vehicleBought'],
      vehicleBought: getDayLessDateTemplate(),
      hasLastSaleDate: false,
    };
  },
  computed: {
    isMobile () { return this.$vuetify.breakpoint.smAndDown; },

    yesNo () { return this.constants.NO_YES.slice().reverse(); },

    vehicleBoughtYearOptions () {
      const { yearOfManufacture } = this.stepData;
      let start = yearOfManufacture && +yearOfManufacture;
      const end = +(new UtcDate()).getFullYear();
      if (!start) start = end - 50;
      // Vehicle Year of Manufacture can be in the future(next year) in some situations (see: https://seopa01.atlassian.net/browse/PU-4377)
      // In that case allow the user to select the current year
      if (start > end) start = end;
      const options = Array(end - start + 1).fill().map((_, idx) => start + idx);
      options.reverse();
      return options;
    },

    isVehiclePurchasedErrorShown () {
      const monthField = this.getFieldFromFormObserver('vehicle-bought_month');
      const yearField = this.getFieldFromFormObserver('vehicle-bought_year');
      const isAnyFieldTouched = (monthField?.touched && monthField?.failed) || (yearField?.touched && yearField?.failed);
      const isPurchasedDateIsValid = !!this.questionData.vehicleBought && new ExtDate(this.questionData.vehicleBought).isValid();

      return this.formObserver.validated ||
      isPurchasedDateIsValid ||
      isAnyFieldTouched;
    },

    isVehiclePurchasedFailed () {
      const fieldObserver = this.formObserver && this.getFieldFromFormObserver('vehicle-bought');
      const fieldErrors = this.formObserver.errors[`${this.stepId}_vehicle-bought`];
      const isObserverHasErrors = !!(this.formObserver.validated && fieldErrors?.length);
      const isPurchasedDateIsValid = fieldObserver?.invalid && !!this.questionData.vehicleBought;

      return isObserverHasErrors ||
      (fieldObserver?.changed && fieldObserver?.invalid) ||
      isPurchasedDateIsValid;
    },
    fieldTrackerId () {
      const snakeVehicleBoughtField = snakeCase(this.questionFields.vehicleBought);
      return {
        vehiclePurchased: snakeCase(this.questionFields.vehiclePurchased),
        month: `${snakeVehicleBoughtField}_month`,
        year: `${snakeVehicleBoughtField}_year`,
      };
    },
  },
  watch: {
    'questionData.vehiclePurchased' (val) {
      if (val) {
        if (!checkDateObjFilled(this.vehicleBought, ['month', 'year'])) {
          this.hasLastSaleDate = true;
          const date = new ExtDate(this.vehicleBought.year, this.vehicleBought.month - 1, 1);
          this.questionData.vehicleBought = date.isValid() ? date.format() : null;
        }
      } else {
        this.hasLastSaleDate = false;
        this.questionData.vehicleBought = null;
        this.vehicleBought = getDayLessDateTemplate();
      }
    },
    /**
     * Update local vehicle bought model on stepData change
     */
    'stepData.vehicleBought' (vehicleBoughtDate) {
      if (vehicleBoughtDate) {
        this.setVehicleBoughtModel(vehicleBoughtDate);
        this.hasLastSaleDate = true;
      } else {
        this.vehicleBought = getDayLessDateTemplate();
        this.hasLastSaleDate = false;
      }
    },

    vehicleBought: {
      handler (val) {
        if (checkDateObjFilled(val, ['month', 'year'])) {
          this.questionData.vehicleBought = null;
          return;
        }
        if (this.questionData.vehiclePurchased) {
          const date = new ExtDate(val.year, val.month - 1, 1);
          this.questionData.vehicleBought = date.isValid() ? date.format() : null;
        }
      },
      deep: true,
    },
  },
  created () {
    const { vehicleBought } = this.stepData;

    if (vehicleBought) this.setVehicleBoughtModel(vehicleBought);

    if (this.lastSaleDate) {
      this.vehicleBought.year = this.lastSaleDate.getFullYear();
      this.vehicleBought.month = this.lastSaleDate.getMonth() + 1;
    }
  },
  methods: {
    setVehicleBoughtModel (vehicleBoughtDate) {
      const date = new UtcDate(vehicleBoughtDate);
      this.vehicleBought.month = date.getMonth() + 1;
      this.vehicleBought.year = date.getFullYear();
    },
    getFieldFromFormObserver (fieldName) {
      return this.formObserver.fields[`${this.stepId}_${fieldName}`];
    },
  },
  setup (props) {
    const dateOfBoughtValidationRules = computed(() => {
      const defaultRules = {
        vehicleBought: {
          yearOfManufacture: props.stepData.yearOfManufacture,
        },
        checkFutureVehicleBought: true,
      };

      return props.validationRules ? { ...props.validationRules.vehicleBought, ...defaultRules } : defaultRules;
    });

    const isWarningTextShown = computed(() => {
      return useTimeZoneWarning(
        props.formObserver,
        `${props.stepId}_vehicle-bought`,
        dateOfBoughtValidationRules.value,
      );
    });

    return {
      ExtDate,
      monthsOptions,
      dateOfBoughtValidationRules,
      isWarningTextShown,
    };
  },
};
</script>

<style lang="scss" scoped>
.dates {
  display: flex;
  justify-content: space-between;

  & > * {
    width: 100%;
    flex: 1 0 0;
    margin: 0 10px;
    &:first-child { margin-left: 0; }
    &:last-child { margin-right: 0; }
  }
}
</style>
